import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, AuthContext } from "../App";
import { toast } from "sonner";
import { BBQ_PRICE_PER_PIT, BBQ_PRICE_BOTH_PITS } from "../constants";
import { 
  ArrowLeft, Flame, Calendar, ChevronLeft, ChevronRight, 
  Clock, AlertTriangle, CheckCircle, Mail, Phone, Home, User, Star, Shield
} from "lucide-react";
import Footer from "../components/Footer";
import LanguageSelector from "../components/LanguageSelector";
import { formatDate } from "../utils/dateFormat";

const BBQBookingPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('calendar');
  const [bookings, setBookings] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    block_unit: "",
    mobile: "",
    email: "",
    pit_selection: "pit1", // pit1, pit2, both
    no_refund_acknowledged: false
  });

  const [blackoutDates, setBlackoutDates] = useState([]);

  useEffect(() => {
    fetchBookings();
    fetchHolidays();
    fetchBlackoutDates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate]);

  const fetchBookings = async () => {
    try {
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1;
      const res = await axios.get(`${API}/bbq/calendar?year=${year}&month=${month}`);
      setBookings(res.data.bookings || []);
    } catch (error) {
      console.error("Failed to fetch BBQ bookings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHolidays = async () => {
    try {
      const res = await axios.get(`${API}/public-holidays`);
      setHolidays(res.data || []);
    } catch (error) {
      console.error("Failed to fetch holidays:", error);
    }
  };

  const fetchBlackoutDates = async () => {
    try {
      const res = await axios.get(`${API}/blackout-dates?facility=bbq`);
      setBlackoutDates(res.data || []);
    } catch (error) {
      console.error("Failed to fetch blackout dates:", error);
    }
  };

  // Check if a date is blacked out for BBQ
  const getBlackoutInfo = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const blackout = blackoutDates.find(b => {
      const start = b.start_date || b.date;
      const end = b.end_date || b.date;
      return dateStr >= start && dateStr <= end;
    });
    if (blackout) {
      return {
        isBlackedOut: true,
        reason: blackout.reason || 'Unavailable'
      };
    }
    return { isBlackedOut: false, reason: null };
  };

  // Check if a date is a public holiday or in-lieu
  const getHolidayInfo = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const holiday = holidays.find(h => h.date === dateStr);
    if (holiday) {
      const isInLieu = holiday.description?.toLowerCase().includes('in lieu') || 
                       holiday.description?.toLowerCase().includes('observed');
      return {
        isHoliday: true,
        isInLieu,
        description: holiday.description
      };
    }
    return { isHoliday: false, isInLieu: false, description: null };
  };

  const getDaysInMonth = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay };
  };

  const { daysInMonth, startingDay } = getDaysInMonth();

  const getBookingsForDay = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return bookings.filter(b => b.date === dateStr);
  };

  const getSlotAvailability = (day, slotType) => {
    const dayBookings = getBookingsForDay(day);
    const slotBookings = dayBookings.filter(b => b.slot_type === slotType && b.status !== 'cancelled');
    
    const pit1Booked = slotBookings.some(b => b.pit_selection === 'pit1' || b.pit_selection === 'both');
    const pit2Booked = slotBookings.some(b => b.pit_selection === 'pit2' || b.pit_selection === 'both');
    
    return {
      pit1Available: !pit1Booked,
      pit2Available: !pit2Booked,
      bothAvailable: !pit1Booked && !pit2Booked,
      fullyBooked: pit1Booked && pit2Booked
    };
  };

  const isPastOrWithin12Hours = (day, slotType) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const slotHour = slotType === 'afternoon' ? 13 : 18;
    const slotDate = new Date(`${dateStr}T${String(slotHour).padStart(2, '0')}:00:00`);
    const now = new Date();
    const hoursDiff = (slotDate - now) / (1000 * 60 * 60);
    
    return hoursDiff < 2;
  };

  const handleSlotClick = (day, slotType) => {
    const availability = getSlotAvailability(day, slotType);
    
    if (availability.fullyBooked) {
      toast.error("Both pits are fully booked for this slot");
      return;
    }
    
    if (isPastOrWithin12Hours(day, slotType)) {
      toast.error("Bookings must be made at least 2 hours in advance");
      return;
    }
    
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    
    setSelectedSlot({
      date: dateStr,
      slotType,
      availability
    });
    setShowBookingForm(true);
  };

  const calculatePrice = () => {
    if (formData.pit_selection === 'both') return BBQ_PRICE_BOTH_PITS;
    return BBQ_PRICE_PER_PIT;
  };

  const handleSubmitBooking = async (e) => {
    e.preventDefault();
    
    if (!formData.no_refund_acknowledged) {
      toast.error("You must acknowledge the no-refund policy");
      return;
    }
    
    try {
      const bookingData = {
        name: formData.name,
        block_unit: formData.block_unit,
        mobile: formData.mobile,
        email: formData.email.toLowerCase(),
        date: selectedSlot.date,
        slot_type: selectedSlot.slotType,
        pit_selection: formData.pit_selection,
        price: calculatePrice()
      };
      
      const result = await axios.post(`${API}/bbq/bookings`, bookingData);
      toast.success(`BBQ Booking created! Reference: ${result.data.booking.booking_code}`);
      
      setShowBookingForm(false);
      setSelectedSlot(null);
      setFormData({
        name: "",
        block_unit: "",
        mobile: "",
        email: "",
        pit_selection: "pit1",
        no_refund_acknowledged: false
      });
      fetchBookings();
      
      // Navigate to confirmation
      navigate(`/bbq/confirmation/${result.data.booking.booking_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    }
  };

  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"];

  const navigateMonth = (direction) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + direction, 1));
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-orange-500 text-white border-b-4 border-black">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/')} className="p-2 hover:bg-white/20 rounded">
                <ArrowLeft size={20} />
              </button>
              <div className="flex items-center gap-2">
                <Flame className="w-8 h-8" />
                <div>
                  <h1 className="text-lg sm:text-xl font-syne font-bold notranslate" translate="no">BBQ PIT BOOKING</h1>
                  <p className="text-xs text-white/80 hidden sm:block notranslate" translate="no">YCK Court Residents&apos; Network</p>
                </div>
              </div>
            </div>
            
            {/* Right side: Language Selector + Admin Link (only if logged in as admin) */}
            <div className="flex items-center gap-2">
              <LanguageSelector variant="dark" />
              {/* Admin Link - Only shown if logged in as admin */}
              {user?.is_admin && (
                <button
                  onClick={() => navigate('/admin')}
                  className="p-2 hover:bg-white/20 rounded transition-colors"
                  title="Admin Panel"
                >
                  <Shield size={18} />
                </button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Info Banner */}
      <div className="bg-orange-50 border-b-2 border-orange-200 px-4 py-3">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm">
            <span className="font-bold text-orange-700">📍 2 <span className="notranslate" translate="no">BBQ Pits</span> Available</span>
            <span className="text-orange-600">• $20 per <span className="notranslate" translate="no">pit</span> per session • 1PM-5PM or 6PM-10PM</span>
          </div>
        </div>
      </div>

      {/* 12-Hour Rule Warning */}
      <div className="bg-amber-50 border-b-2 border-amber-300 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-start gap-2">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <strong>Advance Booking:</strong> Bookings must be made at least 2 hours before your session. 
            No refunds.
          </p>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* View Toggle */}
        <div className="flex justify-center gap-2 mb-6">
          <button
            key={`bbq-calendar-${viewMode}`}
            onClick={() => setViewMode('calendar')}
            className={`px-4 py-2 font-bold text-sm border-2 border-black notranslate ${
              viewMode === 'calendar' ? 'bg-orange-500 text-white' : 'bg-white hover:bg-gray-50'
            }`}
            translate="no"
          >
            📅 CALENDAR
          </button>
          <button
            key={`bbq-list-${viewMode}`}
            onClick={() => setViewMode('list')}
            className={`px-4 py-2 font-bold text-sm border-2 border-black notranslate ${
              viewMode === 'list' ? 'bg-orange-500 text-white' : 'bg-white hover:bg-gray-50'
            }`}
            translate="no"
          >
            📋 LIST VIEW
          </button>
        </div>

        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => navigateMonth(-1)} className="p-2 border-2 border-black hover:bg-gray-100">
            <ChevronLeft size={24} />
          </button>
          <h2 className="text-xl sm:text-2xl font-syne font-bold">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          <button onClick={() => navigateMonth(1)} className="p-2 border-2 border-black hover:bg-gray-100">
            <ChevronRight size={24} />
          </button>
        </div>

        {/* Calendar View */}
        {viewMode === 'calendar' && (
          <div className="border-4 border-black bg-white">
            {/* Day Headers */}
            <div className="grid grid-cols-7 border-b-2 border-black">
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} className="p-2 text-center font-bold text-xs sm:text-sm bg-orange-100 border-r border-black last:border-r-0">
                  {day}
                </div>
              ))}
            </div>
            
            {/* Calendar Grid */}
            <div className="grid grid-cols-7">
              {/* Empty cells for starting day */}
              {Array.from({ length: startingDay }).map((_, idx) => (
                <div key={`empty-${idx}`} className="min-h-[80px] sm:min-h-[100px] border-r border-b border-black bg-gray-50" />
              ))}
              
              {/* Day cells */}
              {Array.from({ length: daysInMonth }).map((_, idx) => {
                const day = idx + 1;
                const afternoonAvail = getSlotAvailability(day, 'afternoon');
                const eveningAvail = getSlotAvailability(day, 'evening');
                const isPast12hrAfternoon = isPastOrWithin12Hours(day, 'afternoon');
                const isPast12hrEvening = isPastOrWithin12Hours(day, 'evening');
                const holidayInfo = getHolidayInfo(day);
                const blackoutInfo = getBlackoutInfo(day);
                
                return (
                  <div key={day} className={`min-h-[80px] sm:min-h-[100px] border-r border-b border-black p-1 sm:p-2 ${
                    blackoutInfo.isBlackedOut ? 'bg-red-50' : holidayInfo.isHoliday ? 'bg-amber-50' : ''
                  }`}>
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-xs sm:text-sm font-bold">{day}</span>
                      {blackoutInfo.isBlackedOut && (
                        <span className="text-xs font-bold px-1 rounded bg-red-200 text-red-700" title={blackoutInfo.reason}>
                          BO
                        </span>
                      )}
                      {holidayInfo.isHoliday && !blackoutInfo.isBlackedOut && (
                        <span className={`text-xs font-bold px-1 rounded ${
                          holidayInfo.isInLieu 
                            ? 'bg-purple-200 text-purple-700' 
                            : 'bg-amber-200 text-amber-700'
                        }`} title={holidayInfo.description}>
                          {holidayInfo.isInLieu ? 'IL' : 'PH'}
                        </span>
                      )}
                    </div>
                    
                    {/* Afternoon Slot */}
                    <button
                      onClick={() => !blackoutInfo.isBlackedOut && handleSlotClick(day, 'afternoon')}
                      disabled={blackoutInfo.isBlackedOut || afternoonAvail.fullyBooked || isPast12hrAfternoon}
                      className={`w-full text-xs p-1 mb-1 border font-bold truncate ${
                        blackoutInfo.isBlackedOut
                          ? 'bg-red-200 text-red-500 cursor-not-allowed border-red-300'
                          : isPast12hrAfternoon
                          ? 'bg-white text-gray-400 cursor-default border-gray-200'
                          : afternoonAvail.fullyBooked
                          ? 'bg-red-100 text-red-600 border-red-300'
                          : afternoonAvail.bothAvailable
                          ? 'bg-green-100 text-green-700 border-green-400 hover:bg-green-200'
                          : 'bg-amber-100 text-amber-700 border-amber-400 hover:bg-amber-200'
                      }`}
                    >
                      {blackoutInfo.isBlackedOut ? 'N/A' : isPast12hrAfternoon ? '1PM' : afternoonAvail.fullyBooked ? 'FULL' : '1PM'}
                    </button>
                    
                    {/* Evening Slot */}
                    <button
                      onClick={() => !blackoutInfo.isBlackedOut && handleSlotClick(day, 'evening')}
                      disabled={blackoutInfo.isBlackedOut || eveningAvail.fullyBooked || isPast12hrEvening}
                      className={`w-full text-xs p-1 border font-bold truncate ${
                        blackoutInfo.isBlackedOut
                          ? 'bg-red-200 text-red-500 cursor-not-allowed border-red-300'
                          : isPast12hrEvening
                          ? 'bg-white text-gray-400 cursor-default border-gray-200'
                          : eveningAvail.fullyBooked
                          ? 'bg-red-100 text-red-600 border-red-300'
                          : eveningAvail.bothAvailable
                          ? 'bg-green-100 text-green-700 border-green-400 hover:bg-green-200'
                          : 'bg-amber-100 text-amber-700 border-amber-400 hover:bg-amber-200'
                      }`}
                    >
                      {blackoutInfo.isBlackedOut ? 'N/A' : isPast12hrEvening ? '6PM' : eveningAvail.fullyBooked ? 'FULL' : '6PM'}
                    </button>
                  </div>
                );
              })}
            </div>
            
            {/* Legend */}
            <div className="mt-4 p-3 bg-gray-50 border-2 border-black">
              <p className="font-bold text-sm mb-2">Legend:</p>
              <div className="flex flex-wrap gap-3 text-xs">
                <span className="flex items-center gap-1"><span className="w-4 h-4 bg-green-100 border border-green-400 rounded"></span> Both Available</span>
                <span className="flex items-center gap-1"><span className="w-4 h-4 bg-amber-100 border border-amber-400 rounded"></span> Partial</span>
                <span className="flex items-center gap-1"><span className="w-4 h-4 bg-red-100 border border-red-300 rounded"></span> Fully Booked</span>
                <span className="flex items-center gap-1"><span className="px-1 bg-amber-200 text-amber-700 font-bold rounded">PH</span> Public Holiday</span>
                <span className="flex items-center gap-1"><span className="px-1 bg-purple-200 text-purple-700 font-bold rounded">IL</span> In-Lieu</span>
                <span className="flex items-center gap-1"><span className="px-1 bg-red-200 text-red-700 font-bold rounded">BO</span> Blackout Date</span>
              </div>
            </div>
          </div>
        )}

        {/* List View */}
        {viewMode === 'list' && (
          <div className="space-y-3">
            {Array.from({ length: daysInMonth }).map((_, idx) => {
              const day = idx + 1;
              const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
              const date = new Date(dateStr);
              const dayName = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][date.getDay()];
              const holidayInfo = getHolidayInfo(day);
              const blackoutInfo = getBlackoutInfo(day);
              
              return ['afternoon', 'evening'].map(slotType => {
                const availability = getSlotAvailability(day, slotType);
                const isPast = isPastOrWithin12Hours(day, slotType);
                
                return (
                  <div key={`${day}-${slotType}`} className={`border-2 border-black p-4 ${
                    blackoutInfo.isBlackedOut ? 'bg-red-50' : holidayInfo.isHoliday ? 'bg-amber-50' : 'bg-white'
                  }`}>
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-mono font-bold text-lg">
                            {dayName}, {day} {monthNames[currentDate.getMonth()]}
                          </p>
                          {blackoutInfo.isBlackedOut && (
                            <span className="px-2 py-0.5 text-xs font-bold rounded flex items-center gap-1 bg-red-200 text-red-700">
                              🚫 BLACKOUT
                            </span>
                          )}
                          {holidayInfo.isHoliday && !blackoutInfo.isBlackedOut && (
                            <span className={`px-2 py-0.5 text-xs font-bold rounded flex items-center gap-1 ${
                              holidayInfo.isInLieu 
                                ? 'bg-purple-200 text-purple-700' 
                                : 'bg-amber-200 text-amber-700'
                            }`}>
                              <Star size={12} fill="currentColor" />
                              {holidayInfo.isInLieu ? 'In-Lieu' : 'PH'}
                            </span>
                          )}
                        </div>
                        {blackoutInfo.isBlackedOut && (
                          <p className="text-xs text-red-600 font-medium">{blackoutInfo.reason}</p>
                        )}
                        {holidayInfo.isHoliday && !blackoutInfo.isBlackedOut && (
                          <p className="text-xs text-amber-700 font-medium">{holidayInfo.description}</p>
                        )}
                        <p className="text-sm text-gray-600">
                          {slotType === 'afternoon' ? '1:00 PM - 5:00 PM' : '6:00 PM - 10:00 PM'}
                        </p>
                        {!blackoutInfo.isBlackedOut && (
                          <div className="flex gap-2 mt-2">
                            <span className={`px-2 py-1 text-xs font-bold rounded ${
                              availability.pit1Available ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                            }`}>
                              Pit 1: {availability.pit1Available ? 'Available' : 'Booked'}
                            </span>
                            <span className={`px-2 py-1 text-xs font-bold rounded ${
                              availability.pit2Available ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-600'
                            }`}>
                              Pit 2: {availability.pit2Available ? 'Available' : 'Booked'}
                            </span>
                          </div>
                        )}
                      </div>
                      
                      {blackoutInfo.isBlackedOut ? (
                        <span className="px-4 py-2 bg-red-200 text-red-600 font-bold text-sm">
                          UNAVAILABLE
                        </span>
                      ) : isPast ? (
                        <span className="px-4 py-2 bg-gray-50 text-gray-400 font-bold text-sm border border-gray-300">
                          PAST
                        </span>
                      ) : availability.fullyBooked ? (
                        <span className="px-4 py-2 bg-red-100 text-red-600 font-bold text-sm">
                          FULLY BOOKED
                        </span>
                      ) : (
                        <button
                          onClick={() => handleSlotClick(day, slotType)}
                          className="px-4 py-2 bg-orange-500 text-white font-bold text-sm border-2 border-black hover:bg-orange-600"
                        >
                          BOOK NOW
                        </button>
                      )}
                    </div>
                  </div>
                );
              });
            })}
          </div>
        )}

        {/* Legend */}
        <div className="mt-6 p-4 bg-gray-50 border-2 border-black">
          <p className="font-bold text-sm mb-2">Legend:</p>
          <div className="flex flex-wrap gap-4 text-xs">
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 bg-green-100 border border-green-400"></span> <span className="notranslate" translate="no">Both Pits</span> Available
            </span>
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 bg-amber-100 border border-amber-400"></span> 1 <span className="notranslate" translate="no">Pit</span> Available
            </span>
            <span className="flex items-center gap-1">
              <span className="w-4 h-4 bg-red-100 border border-red-300"></span> Fully Booked
            </span>
            <span className="flex items-center gap-1">
              <span className="px-1 bg-amber-200 text-amber-700 font-bold rounded">PH</span> Public Holiday
            </span>
            <span className="flex items-center gap-1">
              <span className="px-1 bg-purple-200 text-purple-700 font-bold rounded">IL</span> In-Lieu
            </span>
          </div>
        </div>
      </main>

      {/* Booking Form Modal */}
      {showBookingForm && selectedSlot && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white border-4 border-black max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="bg-orange-500 text-white p-4 border-b-4 border-black">
              <h3 className="text-xl font-bold">🔥 Book <span className="notranslate" translate="no">BBQ Pit Booking</span></h3>
              <p className="text-sm text-white/90">
                {formatDate(selectedSlot.date)} • {selectedSlot.slotType === 'afternoon' ? '1PM-5PM' : '6PM-10PM'}
              </p>
            </div>
            
            {/* Form */}
            <form onSubmit={handleSubmitBooking} className="p-4 space-y-4">
              {/* Email Notice - Bold and Prominent */}
              <div className="bg-blue-100 border-2 border-blue-500 p-4 text-sm">
                <div className="flex items-start gap-2">
                  <Mail className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-bold text-blue-800 text-base">⚠️ VALID EMAIL REQUIRED</p>
                    <p className="text-blue-700 mt-1">Your official receipt and invoice will be sent to your email upon payment verification. Please ensure your email is correct.</p>
                  </div>
                </div>
              </div>
              
              {/* Pit Selection */}
              <div>
                <label className="block font-bold text-sm mb-2">Select <span className="notranslate" translate="no">Pit(s)</span> *</label>
                <div className="grid grid-cols-3 gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, pit_selection: 'pit1'})}
                    disabled={!selectedSlot.availability.pit1Available}
                    className={`p-3 border-2 font-bold text-sm ${
                      formData.pit_selection === 'pit1'
                        ? 'bg-orange-500 text-white border-black'
                        : !selectedSlot.availability.pit1Available
                        ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                        : 'bg-white border-black hover:bg-orange-50'
                    }`}
                  >
                    <span className="notranslate" translate="no">Pit 1</span><br/><span className="text-xs">$20</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, pit_selection: 'pit2'})}
                    disabled={!selectedSlot.availability.pit2Available}
                    className={`p-3 border-2 font-bold text-sm ${
                      formData.pit_selection === 'pit2'
                        ? 'bg-orange-500 text-white border-black'
                        : !selectedSlot.availability.pit2Available
                        ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                        : 'bg-white border-black hover:bg-orange-50'
                    }`}
                  >
                    <span className="notranslate" translate="no">Pit 2</span><br/><span className="text-xs">$20</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, pit_selection: 'both'})}
                    disabled={!selectedSlot.availability.bothAvailable}
                    className={`p-3 border-2 font-bold text-sm ${
                      formData.pit_selection === 'both'
                        ? 'bg-orange-500 text-white border-black'
                        : !selectedSlot.availability.bothAvailable
                        ? 'bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed'
                        : 'bg-white border-black hover:bg-orange-50'
                    }`}
                  >
                    <span className="notranslate" translate="no">Both</span><br/><span className="text-xs">$40</span>
                  </button>
                </div>
              </div>
              
              {/* Contact Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block font-bold text-sm mb-1">
                    <User className="inline w-4 h-4 mr-1" /> Full Name *
                  </label>
                  <input
                    type="text"
                    className="brutal-input w-full notranslate"
                    translate="no"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="block font-bold text-sm mb-1">
                    <Home className="inline w-4 h-4 mr-1" /> Block & Unit *
                  </label>
                  <input
                    type="text"
                    className="brutal-input w-full notranslate"
                    translate="no"
                    placeholder="e.g., Blk 212 #01-1234"
                    value={formData.block_unit}
                    onChange={(e) => setFormData({...formData, block_unit: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="block font-bold text-sm mb-1">
                    <Phone className="inline w-4 h-4 mr-1" /> Mobile Number *
                  </label>
                  <input
                    type="tel"
                    className="brutal-input w-full notranslate"
                    translate="no"
                    placeholder="e.g., 91234567"
                    value={formData.mobile}
                    onChange={(e) => setFormData({...formData, mobile: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="block font-bold text-sm mb-1">
                    <Mail className="inline w-4 h-4 mr-1" /> Email *
                  </label>
                  <input
                    type="email"
                    className="brutal-input w-full notranslate"
                    translate="no"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                  />
                </div>
              </div>
              
              {/* Price Summary */}
              <div className="bg-orange-50 border-2 border-orange-300 p-4">
                <div className="flex justify-between items-center">
                  <span className="font-bold">Total Amount:</span>
                  <span className="text-2xl font-mono font-bold text-orange-600">${calculatePrice()}</span>
                </div>
              </div>
              
              {/* No Refund Acknowledgement */}
              <div className="bg-red-50 border-2 border-red-300 p-4">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.no_refund_acknowledged}
                    onChange={(e) => setFormData({...formData, no_refund_acknowledged: e.target.checked})}
                    className="mt-1 w-5 h-5"
                    required
                  />
                  <span className="text-sm text-red-800">
                    <strong>I understand and agree:</strong> No refunds. Bookings must be made at least 2 hours before the session.
                  </span>
                </label>
              </div>
              
              {/* Buttons */}
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => { setShowBookingForm(false); setSelectedSlot(null); }}
                  className="flex-1 py-3 font-bold border-2 border-black hover:bg-gray-100"
                >
                  CANCEL
                </button>
                <button
                  type="submit"
                  className="flex-1 py-3 font-bold bg-orange-500 text-white border-2 border-black hover:bg-orange-600"
                >
                  CONFIRM BOOKING
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default BBQBookingPage;
